// @givmo/app-admin — the Owner's heads-up before saving Required (AS-BE38 (web), plan (h)).
//
// Jonathan's ruling on D-AS-16, point 8: "an admin heads-up in the portal, not an email — on saving
// Required, a count and a link to the list". The Security tab opens this instead of saving when an
// Owner moves the tier TO Required while the company two-factor requirement is live; Confirm runs
// the tab's existing write, unchanged, and Cancel writes nothing. The count is here; the link comes
// with the saved setting (below).
//
// ══ THE NUMBER IS READ WHEN THIS OPENS ════════════════════════════════════════════════════════
// The tab's reveal probe already carried `employees_without_second_factor`, but once per page load,
// and the count moves as employees link, unlink and add factors. It matters at exactly one moment —
// this one — so it is read again on every opening, as the L4 ceremony reads its counts
// (`sso-lock-ceremony-dialog.tsx`), and while that read is in flight the previous opening's number
// is never shown in its place (`isFetching`, below). The read is the probe's own transport: the same
// route, and its never-rejecting contract is the one a heads-up wants.
//
// ⚠ UNLIKE THE CEREMONY, A MISSING NUMBER DOES NOT BLOCK CONFIRM. L4 can stop people signing in, so
// its counts are a gate. This one cannot strand anyone — every employee it reaches keeps an Unlink
// exit and their personal account — so the count is a heads-up, and "saving is unchanged" holds: a
// read that fails, or answers without the count, says so, offers a retry, and still lets the Owner
// save. What it never does is invent a number, and least of all a zero. Confirm waits for a read
// that answers, but never for one that does not: the api-client sets no request timeout, so the
// read is bounded (`COUNT_READ_TIMEOUT_MS`), and past the bound it is the unread state.
//
// ══ NO LINK BEFORE THE SAVE — THE TAB OFFERS IT AFTER ═════════════════════════════════════════
// The list is the roster's two-factor column (AS-WEB5), which reads the EFFECTIVE tier. Before
// Required is in force every row reads `not_required` and the column is not drawn at all
// (`rosterShowsMfaColumn`). So a link here would leave the dialog, drop the Owner's unsaved choice,
// and land on a list that cannot show anyone yet. This says, as plain text, where the list appears
// once they are being asked; the tab draws the link under the saved setting once Confirm's save has
// succeeded, and never after a failed one (the orchestrator's ruling on cut 1003k's Q3).

import { useQuery } from '@tanstack/react-query';
import { Button, Dialog, testIdProp } from '@givmo/design-system';
import { probeAuthPolicy } from './auth-policy-transport';
import { readEmployeesWithoutSecondFactor } from './company-security-decode';
import { TWO_FACTOR_REQUIREMENT_COPY as COPY, wouldBeAskedLine } from './security-copy';

export const REQUIRE_CONFIRM_TEST_ID = 'settings-security-require-confirm';

/** Its own key: a one-shot read at the moment of decision, never pooled with another query. */
export const SECOND_FACTOR_COUNT_QUERY_KEY = [
  'admin-company-employees-without-second-factor',
] as const;

/**
 * How long the heads-up waits for the count (the cut-1003k review's F4). A read that never answered
 * held Confirm on "Checking who this would affect…" until the fetch itself gave up. Past the bound
 * the opening reads as unread, Confirm enabled; a late answer is dropped.
 */
export const COUNT_READ_TIMEOUT_MS = 8_000;

/** `work`'s answer, or `null` once the bound passes first. `probeAuthPolicy` never rejects. */
function withinBound<T>(work: Promise<T>): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), COUNT_READ_TIMEOUT_MS);
    void work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
    );
  });
}

/** The count, or `null` when this opening could not get one. Never a stand-in zero. */
async function readCountNow(): Promise<number | null> {
  const result = await withinBound(probeAuthPolicy());
  return result !== null && result.available ? readEmployeesWithoutSecondFactor(result.body) : null;
}

export interface TwoFactorRequirementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tab's existing save of the drafted policy. */
  onConfirm: () => void;
  /** The save is in flight: Confirm spins, and nothing may dismiss the dialog under it. */
  submitting: boolean;
  /** Whether the lines saying who is asked carry the L4 lock's exemption (the tab decides). */
  lockExemptionShown: boolean;
}

export function TwoFactorRequirementDialog({
  open,
  onOpenChange,
  onConfirm,
  submitting,
  lockExemptionShown,
}: TwoFactorRequirementDialogProps) {
  const read = useQuery({
    queryKey: SECOND_FACTOR_COUNT_QUERY_KEY,
    queryFn: readCountNow,
    // Only while open: fetching it for every visit to the tab would be a request nobody reads.
    enabled: open,
    // Refetched on every opening. ⚠ The observer lives outside `Dialog.Content`, so it survives a
    // close and a reopened dialog still holds the LAST opening's number in `data` — only
    // `isFetching` tells "current" from "what we showed last time", which is why the checking state
    // below keys on it rather than on `data` being present.
    staleTime: 0,
    gcTime: 0,
  });

  const checking = read.isFetching || read.data === undefined;
  const count = checking ? undefined : read.data;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label={COPY.confirmTitle}
        className="flex flex-col gap-12"
        width={480}
        testId={REQUIRE_CONFIRM_TEST_ID}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {COPY.confirmTitle}
        </Dialog.Title>

        {/* Each sentence is a block SPAN inside the description's `<p>` — the shared confirm dialog's
            idiom (`components/confirm-dialog.tsx`) — so the whole body is the dialog's accessible
            description and no paragraph nests inside another. */}
        <Dialog.Description className="flex flex-col gap-8 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          <ConfirmBody count={count} lockExemptionShown={lockExemptionShown} />
        </Dialog.Description>

        {count === null && (
          <div
            className="flex flex-col items-start gap-4"
            {...testIdProp(`${REQUIRE_CONFIRM_TEST_ID}-count-failed`)}
          >
            <p className="font-sans text-s13 font-w500 tracking-normal text-text-secondary">
              {COPY.confirmCountFailed}
            </p>
            <button
              type="button"
              onClick={() => void read.refetch()}
              className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
              {...testIdProp(`${REQUIRE_CONFIRM_TEST_ID}-count-retry`)}
            >
              {COPY.confirmCountRetry}
            </button>
          </div>
        )}

        {count !== undefined && count !== null && count > 0 && (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
            {...testIdProp(`${REQUIRE_CONFIRM_TEST_ID}-list`)}
          >
            {COPY.confirmList}
          </p>
        )}

        <div className="mt-12 flex flex-wrap items-center justify-end gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
            testId={`${REQUIRE_CONFIRM_TEST_ID}-cancel`}
          >
            {COPY.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={checking}
            loading={submitting}
            onClick={onConfirm}
            testId={`${REQUIRE_CONFIRM_TEST_ID}-confirm`}
          >
            {COPY.confirm}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * The body for each answer the read can give: still reading, a count (zero, one, many), or none.
 * Every branch is a statement about what saving WOULD do; none reports what is happening. The two
 * that speak of employees in general carry the L4 lock's exemption where the tab shows it; the count
 * needs none, since the server's number already leaves those employees out.
 */
function ConfirmBody({
  count,
  lockExemptionShown,
}: {
  count: number | null | undefined;
  lockExemptionShown: boolean;
}) {
  const exemption = lockExemptionShown ? (
    <Line testId="lock-exemption">{COPY.lockExemption}</Line>
  ) : null;
  if (count === undefined) {
    return <Line testId="checking">{COPY.confirmCounting}</Line>;
  }
  if (count === null) {
    return (
      <>
        <Line testId="general">{COPY.confirmGeneral}</Line>
        <Line testId="personal">{COPY.confirmPersonalMany}</Line>
        {exemption}
      </>
    );
  }
  if (count === 0) {
    return (
      <>
        <Line testId="none">{COPY.confirmNone}</Line>
        {exemption}
      </>
    );
  }
  return (
    <>
      <Line testId="count">{wouldBeAskedLine(count)}</Line>
      <Line testId="personal">
        {count === 1 ? COPY.confirmPersonalOne : COPY.confirmPersonalMany}
      </Line>
    </>
  );
}

/** One sentence of the body: a block span, since the description itself is a `<p>`. */
function Line({ testId, children }: { testId: string; children: string }) {
  return (
    <span className="block" {...testIdProp(`${REQUIRE_CONFIRM_TEST_ID}-${testId}`)}>
      {children}
    </span>
  );
}
